/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable react/prop-types */

// 1. Add this module augmentation at the top of the file (or in a global .d.ts)
declare module '@mui/x-data-grid' {
  interface FooterPropsOverrides {
    footer?: React.ReactNode;
    summary?: any;
    rows: any[];
    columns: any[];
    pagination?: boolean;
  }
  interface ToolbarPropsOverrides {
    canExport?: any;
    canPrint?: any;
    search?: any;
    headerProps?: any;
    isHeader?: boolean;
  }
  interface NoRowsOverlayPropsOverrides {
    message?: string;
  }
}

import {
  DataGrid,
  GridLoadingOverlay,
  gridPaginatedVisibleSortedGridRowEntriesSelector,
  GridPagination,
  GridSortModel,
  useGridApiRef,
} from '@mui/x-data-grid';

import React, { useCallback, useMemo, useState } from 'react';

import CustomGridToolbar from './CustomGridToolbar';
import CustomSummary from './DataGridSummary';

import { Theme } from '@mui/material/styles';
import { SxProps } from '@mui/material';
import CustomDataGridNoRowOverlay from './CustomDataGridNoRowOverlay';

// ─────────────────────────────────────────────────────────────────────────────
// Static constants — defined OUTSIDE the component so they are never recreated
// ─────────────────────────────────────────────────────────────────────────────

const BASE_SX: SxProps<Theme> = {
  '& .MuiDataGrid-columnSeparator': {
    display: 'none !important',
  },
  '& .MuiButtonBase-root': {
    backgroundColor: '#0A1A2C',
  },
  '& .MuiDataGrid-cell': {
    borderBottom: 'none',
  },
  '.css-ltf0zy-MuiDataGrid-iconButtonContainer': {
    visibility: 'hidden !important',
  },
  '.MuiDataGrid-columnHeader': {
    backgroundColor: '#0A1A2C',
    padding: '0px 6px !important',
  },
  '.MuiCheckbox-root': {
    backgroundColor: 'transparent !important',
  },
  '& .MuiDataGrid-cell, & .MuiDataGrid-columnHeaderTitle': {
    textOverflow: 'ellipsis',
  },
};

const BASE_SLOT_PROPS = {
  panel: {
    sx: {
      top: '-120px !important',
    },
  },
  toolbar: {
    sx: {
      marginBottom: '20px !important',
    },
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// Slot components — defined OUTSIDE so MUI never sees a new component type
// on re-render (which would cause full unmount/remount of the slot).
// Data is passed via slotProps, not closures.
// ─────────────────────────────────────────────────────────────────────────────

const FooterSlot = ({
  footer,
  summary,
  rows,
  columns,
  pagination,
}: {
  footer?: React.ReactNode;
  summary?: any;
  rows: any[];
  columns: any[];
  pagination?: boolean;
}) => (
  <div className="d-flex flex-1 flex-column">
    {footer && footer}
    {summary && <CustomSummary rows={rows} columns={columns} summary={summary} />}
    {pagination && <GridPagination />}
  </div>
);

const ToolbarSlot = ({
  canExport,
  canPrint,
  search,
  headerProps,
  isHeader,
}: {
  canExport?: any;
  canPrint?: any;
  search?: any;
  headerProps?: any;
  isHeader?: boolean;
}) => (
  <CustomGridToolbar
    canExport={canExport}
    canPrint={canPrint}
    search={search}
    headerProps={headerProps}
    isHeader={isHeader}
  />
);

const NoRowsSlot = ({ message }: { message?: string }) => (
  <CustomDataGridNoRowOverlay message={message || 'No Rows'} />
);

const LoadingSlot = () => <GridLoadingOverlay style={{ backgroundColor: 'rgb(0 0 0 / 29%)' }} />;

// Stable slots object — never recreated because components are defined outside
const SLOTS = {
  footer: FooterSlot,
  toolbar: ToolbarSlot,
  noRowsOverlay: NoRowsSlot,
  loadingOverlay: LoadingSlot,
} as const;

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

interface CustomDatagridProps {
  rows: any[];
  columns: any[];
  rowHeight?: number | 'auto';
  pagination?: boolean;
  summary?: any;
  hiddenCols?: any;
  toolbar?: any;
  loading?: boolean;
  rowSelection?: boolean;
  onEditCellChange?: (params: any) => void;
  pageNumber?: 5 | 10 | 15 | 50 | 100;
  sorting?: GridSortModel;
  canExport?: any;
  canPrint?: any;
  noRowsMessage?: string;
  search?: any;
  footer?: React.ReactNode;
  cellBorder?: boolean;
  isZeroMargin?: boolean;
  headerProps?: any;
  isHeader?: boolean;
  onRowClick?: (params: any) => void;
  apiRef?: any;
  autoHeight?: boolean;
  rowSelectionModel?: any[];
  onRowSelectionModelChange?: (params: any) => void;
  checkboxSelection?: boolean;
  isRowSelectable?: (params: any) => boolean;
  columnVisibilityModel?: any;
  onRowDoubleClick?: (params: any) => void;
  height?: number;
  sortingOrder?: any[];
  onSortModelChange?: (params: any) => void;
}

// ─────────────────────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────────────────────

const CustomDatagrid: React.FC<CustomDatagridProps> = React.memo(
  ({
    rows,
    columns,
    rowHeight,
    pagination,
    summary,
    hiddenCols,
    loading,
    rowSelection,
    onEditCellChange,
    pageNumber,
    sorting,
    canExport,
    canPrint,
    noRowsMessage,
    search,
    footer,
    cellBorder,
    isZeroMargin,
    headerProps,
    isHeader,
    onRowClick,
    apiRef,
    checkboxSelection = false,
    onRowSelectionModelChange,
    isRowSelectable,
    columnVisibilityModel,
    rowSelectionModel,
    onRowDoubleClick,
    height = 23,
    sortingOrder,
    onSortModelChange,
  }: CustomDatagridProps) => {
    const internalApiRef = useGridApiRef();
    const effectiveApiRef = apiRef || internalApiRef;
    const [hasVisibleRows, setHasVisibleRows] = useState(true);

    // ── Memoized rows — prevents new array reference on every render ──────────
    const mappedRows = useMemo(
      () => rows.map((item, index) => ({ id: index + 1, ...item })),
      [rows],
    );

    // ── Memoized callbacks ────────────────────────────────────────────────────
    const getRowHeight = useCallback((): number | 'auto' => rowHeight || 'auto', [rowHeight]);

    const getRowClassName = useCallback((params: any): string => {
      const row = params.row;
      const classes = [
        row.isFullRowNotification ? 'notification-full-row' : '',
        params.indexRelativeToCurrentPage % 2 === 0 ? 'rdg-row-even' : 'rdg-row-odd',
        Number.isInteger(row.status) && [4, 6, 31].includes(row.status) ? 'deleted-row-red' : '',
        row.group ? 'row-roles-group' : 've',
        row.inventoryOf === 2 ? 'vehicle-da-inventory' : '',
        row.statusClass || '',
        row.disable ? 'row-disable' : '',
        row.duplicate ? 'row-duplicate' : '',
        row.failed ? 'row-failed' : '',
      ];
      return classes.filter(Boolean).join(' ');
    }, []); // row data comes through params — no external deps needed

    const handleFilterModelChange = useCallback(() => {
      setTimeout(() => {
        const count =
          gridPaginatedVisibleSortedGridRowEntriesSelector(effectiveApiRef)?.length ?? rows.length;
        setHasVisibleRows(count > 0);
      }, 0);
    }, [effectiveApiRef, rows.length]);

    // ── Memoized sx — recomputed only when relevant props change ─────────────
    const sx = useMemo<SxProps<Theme>>(
      () => ({
        ...BASE_SX,
        '&.MuiDataGrid-root--densityCompact .MuiDataGrid-cell, &.MuiDataGrid-root--densityStandard .MuiDataGrid-cell, &.MuiDataGrid-root--densityComfortable .MuiDataGrid-cell':
          {
            py: isZeroMargin ? '0' : rowHeight === 'auto' ? '15px' : '0.4rem',
            px: isZeroMargin ? '0' : rowHeight === 'auto' ? '15px' : '0.4rem',
            borderRadius: isZeroMargin ? '0.3rem' : '0',
          },
        '& .MuiDataGrid-cell': {
          borderBottom: 'none',
          ...(cellBorder && {
            border: 2,
            borderColor: '#495d73',
            borderRight: 0,
            borderTop: 0,
          }),
        },
        '& .MuiDataGrid-virtualScroller': {
          minHeight: `${rows?.length === 0 || !hasVisibleRows ? '200px' : 'auto'} !important`,
          maxHeight: `calc(100vh - ${height}rem)`,
          overflowY: 'auto',
        },
      }),
      [isZeroMargin, rowHeight, cellBorder, rows?.length, hasVisibleRows, height],
    );

    // ── Memoized slotProps — pass dynamic data here instead of via closures ──
    const slotProps = useMemo(
      () => ({
        ...BASE_SLOT_PROPS,
        footer: { footer, summary, rows, columns, pagination },
        toolbar: { canExport, canPrint, search, headerProps, isHeader },
        noRowsOverlay: { message: noRowsMessage },
      }),
      [
        footer,
        summary,
        rows,
        columns,
        pagination,
        canExport,
        canPrint,
        search,
        headerProps,
        isHeader,
        noRowsMessage,
      ],
    );

    // ── Memoized initialState — only computed once (sorting/hiddenCols are
    //    initial values so they don't need to trigger re-renders) ─────────────
    const initialState = useMemo(
      () => ({
        pagination: {
          paginationModel: { pageSize: pageNumber ?? 100 },
        },
        columns: hiddenCols,
        sorting: { sortModel: sorting },
      }),
      [], // intentionally empty — these are truly initial-only values
    );

    return (
      <div style={{ width: '100%' }}>
        <DataGrid
          style={{ color: 'white', borderStyle: 'none' }}
          rows={mappedRows}
          columns={columns}
          getRowHeight={getRowHeight}
          getRowClassName={getRowClassName}
          processRowUpdate={onEditCellChange}
          checkboxSelection={checkboxSelection}
          disableColumnMenu={true}
          disableRowSelectionOnClick={false}
          onFilterModelChange={handleFilterModelChange}
          apiRef={effectiveApiRef}
          rowSelection={rowSelection}
          onRowClick={onRowClick}
          onRowDoubleClick={onRowDoubleClick}
          getRowId={(row) => row.id}
          pageSizeOptions={[5, 10, 15, 50, 100]}
          loading={loading}
          onSortModelChange={onSortModelChange}
          sortingOrder={sortingOrder || ['asc', 'desc']}
          isRowSelectable={isRowSelectable}
          columnVisibilityModel={columnVisibilityModel}
          rowSelectionModel={rowSelectionModel}
          onRowSelectionModelChange={onRowSelectionModelChange}
          initialState={initialState}
          slots={SLOTS}
          slotProps={slotProps as any}
          sx={sx}
        />
      </div>
    );
  },
);

CustomDatagrid.displayName = 'CustomDatagrid';

export default CustomDatagrid;
